import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Language, LanguageContextType, getText } from '../utils/i18n';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

function getInitialLanguage(): Language {
  const saved = localStorage.getItem('language');
  return saved === 'zh' || saved === 'en' ? saved : 'en';
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
  }, [language]);

  const t = useCallback((key: string): string => getText(key, language), [language]);

  // App 因 useLocation 在每次路由切换时重渲染，value 必须稳定，
  // 否则所有 useLanguage 消费者都会被无意义地重渲染
  const value = useMemo<LanguageContextType>(
    () => ({ language, setLanguage, t }),
    [language, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
